import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";

interface FormData {
  treatmentId: string;
  patientId: string | null;
  userName: string;
  regno: string;
  paymentid: string;
  age: number;
  total: number;
  lastDue: number;
  payable: number;
  discount: number;
  netotal: number;
  due: number;
  card: number;
  cash: number;
  bankTransfer: number;
  cardLast4: string;
  paymentDate: string;
}

interface PaysProps {
  formData: FormData;
  edit: boolean;
  setformData: (data: FormData) => void;
  closePay: () => void;
  handleSave: (e: any, print?: boolean) => void;
  smsDisabled?: boolean;
  smsDisabledMessage?: string;
}

const Pays: React.FC<PaysProps> = ({
  formData,
  setformData,
  handleSave,
  closePay,
  edit,
  smsDisabled = false,
  smsDisabledMessage = "SMS tokens finished — buy a package from My Account.",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const loaderActive = useSelector(
    (state: { loader: { active: boolean } }) => state.loader.active
  );

  useEffect(() => {
    if (isSubmitting && !loaderActive) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [loaderActive, isSubmitting]);

  const submitPayment = (sms: boolean, print?: boolean) => {
    if (isSubmittingRef.current) return;
    if (
      formData.cash === 0 &&
      formData.card === 0 &&
      formData.bankTransfer === 0
    ) {
      toast("You Cannot Pay With Zero Amount or empty");
      return;
    }
    if (formData.card > 0) {
      const last4 = String(formData.cardLast4 ?? "").replace(/\D/g, "");
      if (last4.length !== 4) {
        toast("Enter the last 4 digits of the card");
        return;
      }
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    handleSave(sms, print);
  };

  const updateFormData = (updates: Partial<FormData>) => {
    const newFormData = { ...formData, ...updates };
    newFormData.netotal = newFormData.payable - newFormData.discount;
    newFormData.due =
      newFormData.netotal -
      newFormData.cash -
      newFormData.card -
      newFormData.bankTransfer;
    if (!(newFormData.card > 0)) {
      newFormData.cardLast4 = "";
    }
    setformData(newFormData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "paymentDate") {
      updateFormData({ paymentDate: value });
      return;
    }
    if (name === "cardLast4") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      updateFormData({ cardLast4: digits });
      return;
    }
    updateFormData({ [name]: parseFloat(value) || 0 });
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitPayment(false);
  };

  const onSubmitSms = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (smsDisabled) {
      toast(smsDisabledMessage);
      return;
    }
    submitPayment(true);
  };

  const handlePrint = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    submitPayment(!smsDisabled, true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <PaymentIcon className="text-primary" sx={{ fontSize: 18 }} />
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200">Payment</h2>
          </div>
          <button
            onClick={closePay}
            className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-600 hover:bg-slate-200 dark:hover:bg-slate-500 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 space-y-3">
          {/* Amount Display */}
          <div className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Amount</span>
            <span className="text-sm font-bold text-slate-800 dark:text-slate-200">Rs {formData.payable}</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Payment Date
            </label>
            <input
              type="date"
              name="paymentDate"
              value={formData.paymentDate || ""}
              onChange={handleInputChange}
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-500 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 dark:text-slate-200"
            />
          </div>

          {/* Discount Input */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Discount</label>
            <input
              type="number"
              name="discount"
              value={formData.discount || ""}
              onChange={handleInputChange}
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-500 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 dark:text-slate-200"
            />
          </div>

          {/* Net Total Display */}
          <div className="flex items-center justify-between p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Net Total</span>
            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">Rs {formData.netotal}</span>
          </div>

          {/* Payment Methods */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">Card</label>
              <input
                type="number"
                name="card"
                value={formData.card || ""}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 rounded-md border border-gray-600 dark:border-slate-500 text-xs focus:outline-none bg-slate-50 dark:bg-slate-700 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">Cash</label>
              <input
                type="number"
                name="cash"
                value={formData.cash || ""}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 rounded-md border border-gray-600 dark:border-slate-500 text-xs focus:outline-none bg-slate-50 dark:bg-slate-700 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">Transfer</label>
              <input
                type="number"
                name="bankTransfer"
                value={formData.bankTransfer || ""}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 rounded-md border border-gray-600 dark:border-slate-500 text-xs focus:outline-none bg-slate-50 dark:bg-slate-700 dark:text-slate-200"
              />
            </div>
          </div>

          {formData.card > 0 ? (
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                Card last 4 digits
              </label>
              <input
                type="text"
                name="cardLast4"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="••••"
                value={formData.cardLast4 || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 rounded-lg border border-indigo-300 dark:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all text-xs tracking-[0.3em] bg-indigo-50/60 dark:bg-slate-700 dark:text-slate-200"
              />
              <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                Required when paying by card
              </p>
            </div>
          ) : null}

          {/* Due/Advanced Display */}
          <div className={`flex items-center justify-between p-2.5 rounded-lg ${formData.due > 0 ? "bg-red-50 dark:bg-red-900/30" : "bg-emerald-50 dark:bg-emerald-900/30"}`}>
            <span className={`text-xs font-medium ${formData.due > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>
              {formData.due > 0 ? "Due" : "Advanced"}
            </span>
            <span className={`text-sm font-bold ${formData.due > 0 ? "text-red-700 dark:text-red-300" : "text-emerald-700 dark:text-emerald-300"}`}>
              Rs {Math.abs(formData.due)}
            </span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onSubmitSms}
              disabled={isSubmitting || smsDisabled}
              title={smsDisabled ? smsDisabledMessage : undefined}
              className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
            >
              Proceed + SMS
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-amber-500"
            >
              Print
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-white font-medium hover:bg-primaryDark transition-all text-xs disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
            >
              {isSubmitting ? "Saving…" : edit ? "Update" : "Proceed"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Pays;
